import React, { useState, useMemo } from 'react';
import { HiCollection, HiEye, HiEyeOff } from 'react-icons/hi';

const HeatmapCurves = ({ heatmapData, maxIntensity }) => {
    const [selectedCurve, setSelectedCurve] = useState({ type: 'horizontal', index: 1 });

    // Função para extrair curvas do heatmap
    const extractCurves = useMemo(() => {
        if (!heatmapData || heatmapData.length === 0) return [];

        const curves = {
            horizontal: [], 
            vertical: [],   
            diagonal: [], 
            diagonalSecondary: []
        };

        // Organizar dados em matriz para facilitar acesso
        const matrix = {};
        heatmapData.forEach(cell => {
            if (!matrix[cell.trl]) matrix[cell.trl] = {};
            matrix[cell.trl][cell.crl] = cell.value;
        });

        // Curvas horizontais (para cada TRL)
        for (let trl = 1; trl <= 9; trl++) {
            const curve = [];
            let sumOfRow = 0;

            // Primeiro, calcular a soma da linha
            for (let crl = 1; crl <= 9; crl++) {
                const rawValue = matrix[trl] && matrix[trl][crl] ? matrix[trl][crl] : 0;
                sumOfRow += rawValue;
            }

            // Depois, normalizar pela soma da linha
            for (let crl = 1; crl <= 9; crl++) {
                const rawValue = matrix[trl] && matrix[trl][crl] ? matrix[trl][crl] : 0;
                const normalizedValue = sumOfRow > 0 ? rawValue / sumOfRow : 0;
                curve.push({
                    x: crl,
                    y: normalizedValue,
                    rawValue: rawValue,
                    label: `CRL ${crl}`,
                    originalCoords: { trl, crl }
                });
            }
            curves.horizontal.push({
                index: trl,
                label: `TRL ${trl}`,
                data: curve
            });
        }

        // Curvas verticais (para cada CRL)
        for (let crl = 1; crl <= 9; crl++) {
            const curve = [];
            let sumOfColumn = 0;

            // Primeiro, calcular a soma da coluna
            for (let trl = 1; trl <= 9; trl++) {
                const rawValue = matrix[trl] && matrix[trl][crl] ? matrix[trl][crl] : 0;
                sumOfColumn += rawValue;
            }

            // Depois, normalizar pela soma da coluna
            for (let trl = 1; trl <= 9; trl++) {
                const rawValue = matrix[trl] && matrix[trl][crl] ? matrix[trl][crl] : 0;
                const normalizedValue = sumOfColumn > 0 ? rawValue / sumOfColumn : 0;
                curve.push({
                    x: trl,
                    y: normalizedValue,
                    rawValue: rawValue,
                    label: `TRL ${trl}`,
                    originalCoords: { trl, crl }
                });
            }
            curves.vertical.push({
                index: crl,
                label: `CRL ${crl}`,
                data: curve
            });
        }

        // Diagonal principal (TRL = CRL)
        const diagonalMain = [];
        let sumOfDiagonalMain = 0;

        // Primeiro, calcular a soma da diagonal principal
        for (let i = 1; i <= 9; i++) {
            const rawValue = matrix[i] && matrix[i][i] ? matrix[i][i] : 0;
            sumOfDiagonalMain += rawValue;
        }

        // Depois, normalizar pela soma da diagonal
        for (let i = 1; i <= 9; i++) {
            const rawValue = matrix[i] && matrix[i][i] ? matrix[i][i] : 0;
            const normalizedValue = sumOfDiagonalMain > 0 ? rawValue / sumOfDiagonalMain : 0;
            diagonalMain.push({
                x: i,
                y: normalizedValue,
                rawValue: rawValue,
                label: `${i}`,
                originalCoords: { trl: i, crl: i }
            });
        }
        curves.diagonal.push({
            index: 1,
            label: 'Diagonal Principal (TRL=CRL)',
            data: diagonalMain
        });

        // Diagonal secundária (TRL + CRL = 10)
        const diagonalSec = [];
        let sumOfDiagonalSecondary = 0;

        // Primeiro, calcular a soma da diagonal secundária
        for (let i = 1; i <= 9; i++) {
            const trl = i;
            const crl = 10 - i;
            const rawValue = matrix[trl] && matrix[trl][crl] ? matrix[trl][crl] : 0;
            sumOfDiagonalSecondary += rawValue;
        }

        // Depois, normalizar pela soma da diagonal
        for (let i = 1; i <= 9; i++) {
            const trl = i;
            const crl = 10 - i;
            const rawValue = matrix[trl] && matrix[trl][crl] ? matrix[trl][crl] : 0;
            const normalizedValue = sumOfDiagonalSecondary > 0 ? rawValue / sumOfDiagonalSecondary : 0;
            diagonalSec.push({
                x: i,
                y: normalizedValue,
                rawValue: rawValue,
                label: `${trl},${crl}`,
                originalCoords: { trl, crl }
            });
        }
        curves.diagonalSecondary.push({
            index: 1,
            label: 'Diagonal Secundária (TRL+CRL=10)',
            data: diagonalSec
        });

        return curves;
    }, [heatmapData]);

    // Obter dados da curva atual
    const currentCurveData = useMemo(() => {
        if (selectedCurve.type === 'diagonal') {
            return extractCurves.diagonal[0] || { label: '', data: [] };
        } else if (selectedCurve.type === 'diagonalSecondary') {
            return extractCurves.diagonalSecondary[0] || { label: '', data: [] };
        } else {
            const curves = extractCurves[selectedCurve.type] || [];
            return curves[selectedCurve.index - 1] || { label: '', data: [] };
        }
    }, [extractCurves, selectedCurve]);

    // Função para obter cor baseada na intensidade (colormap viridis)
    const getHeatmapColor = (value) => {
        const t = value / maxIntensity;
        const viridis = [
            { pos: 0.000, r: 68, g: 1, b: 84 },
            { pos: 0.125, r: 72, g: 40, b: 120 },
            { pos: 0.250, r: 62, g: 73, b: 137 },
            { pos: 0.375, r: 49, g: 104, b: 142 },
            { pos: 0.500, r: 38, g: 130, b: 142 },
            { pos: 0.625, r: 31, g: 158, b: 137 },
            { pos: 0.750, r: 53, g: 183, b: 121 },
            { pos: 0.875, r: 109, g: 205, b: 89 },
            { pos: 1.000, r: 253, g: 231, b: 37 }
        ];

        let color1, color2, localT;
        for (let i = 0; i < viridis.length - 1; i++) {
            if (t >= viridis[i].pos && t <= viridis[i + 1].pos) {
                color1 = viridis[i];
                color2 = viridis[i + 1];
                localT = (t - color1.pos) / (color2.pos - color1.pos);
                break;
            }
        }

        if (!color1) {
            color1 = viridis[viridis.length - 1];
            color2 = viridis[viridis.length - 1];
            localT = 0;
        }

        const r = Math.round(color1.r + (color2.r - color1.r) * localT);
        const g = Math.round(color1.g + (color2.g - color1.g) * localT);
        const b = Math.round(color1.b + (color2.b - color1.b) * localT);

        return `rgb(${r}, ${g}, ${b})`;
    };

    // Função para renderizar o heatmap com ícones de olho
    const renderHeatmap = () => {
        if (!heatmapData || heatmapData.length === 0) {
            return (
                <div className="bg-white border border-gray-200 rounded-lg p-12">
                    <div className="text-center text-gray-500">
                        <HiCollection className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>Nenhum dado para heatmap</p>
                    </div>
                </div>
            );
        }

        const width = 320;
        const height = 290;
        const margin = { top: 40, right: 60, bottom: 60, left: 60 };
        const plotWidth = width - margin.left - margin.right;
        const plotHeight = height - margin.top - margin.bottom;
        const cellSize = plotWidth / 9;

        return (
            <div>
                <svg width={width} height={height}>
                    <g transform={`translate(${margin.left}, ${margin.top})`}>
                        {/* Células do heatmap */}
                        {heatmapData.map((cell, idx) => {
                            const x = (cell.crl - 1) * cellSize;
                            const y = plotHeight - cell.trl * cellSize;
                            const color = getHeatmapColor(cell.value);

                            return (
                                <rect
                                    key={`heatmap-cell-${idx}`}
                                    x={x}
                                    y={y}
                                    width={cellSize}
                                    height={cellSize}
                                    fill={color}
                                    stroke="#3b82f6"
                                    strokeWidth="0.5"
                                // opacity="0.7"
                                >
                                    <title>TRL {cell.trl}, CRL {cell.crl}: {cell.value}</title>
                                </rect>
                            );
                        })}

                        {/* Linhas de destaque para seleção */}
                        {selectedCurve.type === 'horizontal' && (
                            <line
                                x1={0}
                                y1={plotHeight - (selectedCurve.index - 0.5) * cellSize}
                                x2={plotWidth}
                                y2={plotHeight - (selectedCurve.index - 0.5) * cellSize}
                                stroke="#ff6b35"
                                strokeWidth="3"
                                opacity="0.8"
                            />
                        )}

                        {selectedCurve.type === 'vertical' && (
                            <line
                                x1={(selectedCurve.index - 0.5) * cellSize}
                                y1={0}
                                x2={(selectedCurve.index - 0.5) * cellSize}
                                y2={plotHeight}
                                stroke="#ff6b35"
                                strokeWidth="3"
                                opacity="0.8"
                            />
                        )}

                        {selectedCurve.type === 'diagonal' && (
                            <line
                                x1={0}
                                y1={plotHeight}
                                x2={plotWidth}
                                y2={0}
                                stroke="#ff6b35"
                                strokeWidth="3"
                                opacity="0.8"
                            />
                        )}

                        {selectedCurve.type === 'diagonalSecondary' && (
                            <line
                                x1={0}
                                y1={0}
                                x2={plotWidth}
                                y2={plotHeight}
                                stroke="#ff6b35"
                                strokeWidth="3"
                                opacity="0.8"
                            />
                        )}

                        {/* Labels do eixo X (CRL) */}
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => (
                            <text
                                key={`x-label-${i}`}
                                x={(i - 0.5) * cellSize}
                                y={plotHeight + 20}
                                textAnchor="middle"
                                fontSize="12"
                                fill="#4b5563"
                            >
                                {i}
                            </text>
                        ))}

                        {/* Labels do eixo Y (TRL) */}
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => (
                            <text
                                key={`y-label-${i}`}
                                x={-10}
                                y={plotHeight - (i - 0.5) * cellSize + 5}
                                textAnchor="end"
                                fontSize="12"
                                fill="#4b5563"
                            >
                                {i}
                            </text>
                        ))}

                        {/* Ícones de olho para linhas horizontais (TRL) - ao lado dos números */}
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(trl => {
                            const isActive = selectedCurve.type === 'horizontal' && selectedCurve.index === trl;
                            const y = plotHeight - (trl - 0.5) * cellSize;

                            return (
                                <g key={`eye-h-${trl}`}>
                                    <foreignObject
                                        x={-35}
                                        y={y - 8}
                                        width="16"
                                        height="16"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setSelectedCurve({ type: 'horizontal', index: trl })}
                                    >
                                        {isActive ? (
                                            <HiEye className="w-4 h-4 text-blue-600" />
                                        ) : (
                                            <HiEyeOff className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                                        )}
                                    </foreignObject>
                                    <title>TRL {trl} (Horizontal)</title>
                                </g>
                            );
                        })}

                        {/* Ícones de olho para colunas verticais (CRL) - ao lado dos números */}
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(crl => {
                            const isActive = selectedCurve.type === 'vertical' && selectedCurve.index === crl;
                            const x = (crl - 0.5) * cellSize;

                            return (
                                <g key={`eye-v-${crl}`}>
                                    <foreignObject
                                        x={x - 8}
                                        y={plotHeight + 25}
                                        width="16"
                                        height="16"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setSelectedCurve({ type: 'vertical', index: crl })}
                                    >
                                        {isActive ? (
                                            <HiEye className="w-4 h-4 text-blue-600" />
                                        ) : (
                                            <HiEyeOff className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                                        )}
                                    </foreignObject>
                                    <title>CRL {crl} (Vertical)</title>
                                </g>
                            );
                        })}

                        {/* Ícone de olho para diagonal principal */}
                        {/* <g>
                                <foreignObject
                                    x={plotWidth + 8}
                                    y={-18}
                                    width="16"
                                    height="16"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setSelectedCurve({ type: 'diagonal', index: 1 })}
                                >
                                    {selectedCurve.type === 'diagonal' ? (
                                        <HiEye className="w-4 h-4 text-blue-600" />
                                    ) : (
                                        <HiEyeOff className="w-4 h-4 text-gray-400" />
                                    )}
                                </foreignObject>
                                <title>Diagonal Principal</title>
                            </g> */}

                        {/* Ícone de olho para diagonal secundária */}
                        {/* <g>
                                <foreignObject
                                    x={plotWidth + 8}
                                    y={190}
                                    width="16"
                                    height="16"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setSelectedCurve({ type: 'diagonalSecondary', index: 1 })}
                                >
                                    {selectedCurve.type === 'diagonalSecondary' ? (
                                        <HiEye className="w-4 h-4 text-blue-600" />
                                    ) : (
                                        <HiEyeOff className="w-4 h-4 text-gray-400" />
                                    )}
                                </foreignObject>
                                <title>Diagonal Secundária</title>
                            </g> */}

                        {/* Label do eixo X */}
                        <text
                            x={plotWidth / 2}
                            y={plotHeight + 55}
                            textAnchor="middle"
                            fontSize="14"
                            fontWeight="600"
                            fill="#1f2937"
                        >
                            CRL
                        </text>

                        {/* Label do eixo Y */}
                        <text
                            x={-45}
                            y={plotHeight / 2}
                            textAnchor="middle"
                            fontSize="14"
                            fontWeight="600"
                            fill="#1f2937"
                            transform={`rotate(-90, -45, ${plotHeight / 2})`}
                        >
                            TRL
                        </text>
                    </g>
                </svg>
            </div>
        );
    };

    // Função para renderizar a curva
    const renderCurve = () => {
        if (!currentCurveData.data || currentCurveData.data.length === 0) {
            return (
                <div className="text-center text-gray-500 p-12">
                    <HiCollection className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>Nenhum dado para curva</p>
                </div>
            );
        }

        const width = 750;
        const height = 400;
        const margin = { top: 40, right: 20, bottom: 60, left: 60 };
        const plotWidth = width - margin.left - margin.right;
        const plotHeight = height - margin.top - margin.bottom;

        // Escalas - valores normalizados [0, 1]
        const maxY = 1.0;
        const n = currentCurveData.data.length; // sempre 9
        const barPadding = 0.2; // proporção de espaço entre barras
        const barTotalWidth = plotWidth / n;
        const barWidth = barTotalWidth * (1 - barPadding);
        const barOffset = barTotalWidth * (barPadding / 2);
        const xBarLeft = (i) => i * barTotalWidth + barOffset; // posição x da barra (index 0-based)
        const xBarCenter = (i) => xBarLeft(i) + barWidth / 2;
        const yScale = (y) => plotHeight - (y / maxY) * plotHeight;

        return (
            <div>
                <svg width={width} height={height}>
                    <g transform={`translate(${margin.left}, ${margin.top})`}>
                        {/* Grid horizontal */}
                        {[0, 0.2, 0.4, 0.6, 0.8, 1.0].map(t => (
                            <line
                                key={`grid-y-${t}`}
                                x1={0}
                                y1={yScale(t)}
                                x2={plotWidth}
                                y2={yScale(t)}
                                stroke="#f3f4f6"
                                strokeWidth="1"
                            />
                        ))}

                        {/* Barras do histograma */}
                        {currentCurveData.data.map((point, i) => {
                            const barH = plotHeight - yScale(point.y);
                            return (
                                <g key={`bar-${i}`}>
                                    <rect
                                        x={xBarLeft(i)}
                                        y={yScale(point.y)}
                                        width={barWidth}
                                        height={barH}
                                        fill="#3b82f6"
                                        fillOpacity="0.7"
                                        stroke="#3b82f6"
                                        strokeWidth="1"
                                    />
                                    <title>{point.label}: {point.y.toFixed(3)} (normalizado) | {point.rawValue} (original)</title>
                                </g>
                            );
                        })}

                        {/* Eixos */}
                        <line x1={0} y1={plotHeight} x2={plotWidth} y2={plotHeight} stroke="#6b7280" strokeWidth="2" />
                        <line x1={0} y1={0} x2={0} y2={plotHeight} stroke="#6b7280" strokeWidth="2" />

                        {/* Labels do eixo X */}
                        {currentCurveData.data.map((point, i) => (
                            <text
                                key={`x-label-${i}`}
                                x={xBarCenter(i)}
                                y={plotHeight + 20}
                                textAnchor="middle"
                                fontSize="12"
                                fill="#4b5563"
                            >
                                {point.x}
                            </text>
                        ))}

                        {/* Labels do eixo Y - valores normalizados */}
                        {[0, 0.2, 0.4, 0.6, 0.8, 1.0].map(t => (
                            <text
                                key={`y-label-${t}`}
                                x={-10}
                                y={yScale(t) + 4}
                                textAnchor="end"
                                fontSize="12"
                                fill="#4b5563"
                            >
                                {t.toFixed(1)}
                            </text>
                        ))}

                        {/* Label do eixo X */}
                        <text
                            x={plotWidth / 2}
                            y={plotHeight + 45}
                            textAnchor="middle"
                            fontSize="14"
                            fontWeight="500"
                            fill="#1f2937"
                        >
                            {selectedCurve.type === 'horizontal' ? 'CRL' :
                                selectedCurve.type === 'vertical' ? 'TRL' :
                                    selectedCurve.type === 'diagonal' ? 'TRL=CRL' : 'Posição'}
                        </text>

                        {/* Label do eixo Y */}
                        <text
                            x={-40}
                            y={plotHeight / 2}
                            textAnchor="middle"
                            fontSize="14"
                            fontWeight="500"
                            fill="#1f2937"
                            transform={`rotate(-90, -40, ${plotHeight / 2})`}
                        >
                            Distribuição de ocorrências normalizada
                        </text>
                    </g>
                </svg>
            </div>
        );
    };

    if (!heatmapData || heatmapData.length === 0) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-12">
                <div className="text-center text-gray-500">
                    <HiCollection className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>Nenhum dado disponível para curvas</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="text-center font-bold">
                {currentCurveData.label || "Selecione uma curva"}
            </div>
            <div className="flex gap-4">

                {/* Curva (principal) */}
                <div className="flex-1">
                    {renderCurve()}
                </div>

                {/* Heatmap (lado direito, sem sobreposição) */}
                <div className="flex-1">
                    {renderHeatmap()}
                </div>
            </div>
        </>
    );
};

export default HeatmapCurves;